import { useCallback, useRef, useState } from 'react';

import { fetchAvailability } from '../services/createUserService';

export const DEFAULT_PHONE_COUNTRY_CODE = '1';

/** A blank wizard. Also what CREATE NEW USER resets to on the success panel. */
export const emptyDraft = () => ({
  accountIds: [],
  // User Details
  firstName: '',
  lastName: '',
  jobRole: '',
  dmsId: '',
  email: '',
  mobileCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  mobile: '',
  // Additional Info
  address1: '',
  address2: '',
  countryCode: '',
  state: '',
  city: '',
  postalCode: '',
  phone: '',
  fax: '',
  // Permissions & Discoverability
  permissionIds: [],
  managementRoleId: '',
  hiddenExternal: false,
  hiddenInternal: false,
});

// Deliberately loose: the server and the User model are the authority on what a
// valid address is. This only stops the wizard asking about an obvious non-email.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What the server said about the two fields it is asked about on blur.
 *
 * `mobileValid` starts true because an untouched field is not wrong yet — the
 * warning should appear once there is something to warn about, not on arrival.
 */
export const emptyChecks = () => ({
  emailTaken: false,
  mobileTaken: false,
  mobileValid: true,
});

/**
 * State and validation for the Create User wizard (PD-4064).
 *
 * Separate from the panels so the rules are testable without rendering the
 * Wizard, and because some of them are the server's: the email and the mobile are
 * checked against it as the admin leaves each field, and anything it rejects at
 * create time comes back to the field it belongs to (see ../components/
 * CreateUserWizard/serverErrors).
 *
 * Phone validity deliberately is not reimplemented here. The rules are Phonelib's,
 * per country, and a copy in the browser would disagree with the model — so the
 * blur check asks instead.
 *
 * Required fields follow the design's asterisks — Account, First Name, Last Name
 * and Email — not the ticket's prose, which also lists fields the mockup puts on
 * the explicitly optional Additional Info step.
 */
const useCreateUserForm = () => {
  const [draft, setDraft] = useState(emptyDraft);
  const [checks, setChecks] = useState(emptyChecks);
  // Field messages from a rejected create, keyed by draft field.
  const [serverMessages, setServerMessages] = useState({});

  const setField = useCallback((field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    // Editing a field answers whatever the server said about it, so the stale
    // complaint goes away as soon as the admin acts on it.
    setServerMessages((current) =>
      current[field] === undefined
        ? current
        : { ...current, [field]: undefined },
    );
  }, []);

  const reset = useCallback(() => {
    setDraft(emptyDraft());
    setChecks(emptyChecks());
    setServerMessages({});
  }, []);

  /**
   * Changing the Country invalidates the State: the states offered come from the
   * country, so a state chosen under the previous one is not in the new list.
   * Clearing it is what surfaces the design's "Choose a State" warning.
   */
  const setCountryCode = useCallback((value) => {
    setDraft((current) =>
      current.countryCode === value
        ? current
        : { ...current, countryCode: value, state: '' },
    );
  }, []);

  // A stale response must not overwrite a newer one — the admin can retype the
  // email faster than the check comes back.
  const requestId = useRef(0);

  const checkAvailability = useCallback(async (fields) => {
    const id = requestId.current + 1;
    requestId.current = id;

    // Only the fields asked about are updated, so an email check cannot clear a
    // mobile warning and vice versa.
    const merge = (result) =>
      setChecks((current) => ({
        emailTaken: 'email' in fields ? result.emailTaken : current.emailTaken,
        mobileTaken:
          'mobile' in fields ? result.mobileTaken : current.mobileTaken,
        mobileValid:
          'mobile' in fields ? result.mobileValid : current.mobileValid,
      }));

    try {
      const result = await fetchAvailability(fields);
      if (requestId.current === id) merge(result);
    } catch {
      // A failed check must not block the admin: the create itself still rejects
      // a duplicate or malformed value, and that rejection now lands on the field
      // it belongs to. So the worst case is a late error, not a wizard that
      // cannot be finished.
      if (requestId.current === id) {
        merge({ emailTaken: false, mobileTaken: false, mobileValid: true });
      }
    }
  }, []);

  const checkEmail = useCallback(() => {
    if (!EMAIL_PATTERN.test(draft.email.trim())) {
      setChecks((current) => ({ ...current, emailTaken: false }));
      return;
    }

    checkAvailability({ email: draft.email.trim() });
  }, [checkAvailability, draft.email]);

  const checkMobile = useCallback(() => {
    if (draft.mobile.trim() === '') {
      setChecks((current) => ({
        ...current,
        mobileTaken: false,
        mobileValid: true,
      }));
      return;
    }

    checkAvailability({
      mobile: draft.mobile.trim(),
      mobileCountryCode: draft.mobileCountryCode,
    });
  }, [checkAvailability, draft.mobile, draft.mobileCountryCode]);

  // A State is only asked for once a Country has been chosen, and only when that
  // country has states to offer.
  const isStateMissing = (statesByCountry) =>
    Boolean(draft.countryCode) &&
    (statesByCountry[draft.countryCode] || []).length > 0 &&
    !draft.state;

  const stepValidity = (statesByCountry = {}) => [
    draft.accountIds.length > 0,
    Boolean(draft.firstName.trim()) &&
      Boolean(draft.lastName.trim()) &&
      EMAIL_PATTERN.test(draft.email.trim()) &&
      !checks.emailTaken &&
      !checks.mobileTaken &&
      checks.mobileValid,
    !isStateMissing(statesByCountry),
    // A group's chosen role counts as a permission: ticking only Management Role
    // leaves permissionIds empty but is a valid selection.
    draft.permissionIds.length > 0 || Boolean(draft.managementRoleId),
  ];

  return {
    checks,
    checkEmail,
    checkMobile,
    draft,
    serverMessages,
    setServerMessages,
    isStateMissing,
    reset,
    setCountryCode,
    setField,
    stepValidity,
  };
};

/**
 * The request body Management::UsersController#create expects.
 *
 * Blank optional fields are sent as empty strings rather than omitted, so the
 * shape does not change with what the admin filled in. Empty strings normalize to
 * NULL on the model.
 */
export const toPayload = (draft) => ({
  user: {
    first_name: draft.firstName.trim(),
    last_name: draft.lastName.trim(),
    email: draft.email.trim(),
    job_role: draft.jobRole,
    dms_id: draft.dmsId.trim(),
    mobile: draft.mobile.trim(),
    // Only meaningful alongside a number, and the model clears it without one.
    mobile_country_code: draft.mobile.trim() ? draft.mobileCountryCode : '',
    address1: draft.address1.trim(),
    address2: draft.address2.trim(),
    country_code: draft.countryCode,
    state: draft.state,
    city: draft.city.trim(),
    postal_code: draft.postalCode.trim(),
    phone: draft.phone.trim(),
    fax: draft.fax.trim(),
  },
  account_ids: draft.accountIds,
  // The management role is one of the permission rows, so it joins the rest
  // rather than travelling separately.
  role_ids: [...draft.permissionIds, draft.managementRoleId].filter(Boolean),
  discoverability: {
    hidden_external: draft.hiddenExternal,
    hidden_internal: draft.hiddenInternal,
  },
});

export default useCreateUserForm;
